import type {
  ADMIN_DEFAULT_PAGE_SIZE,
  ADMIN_PAGE_SIZES,
  AdminAuditAction,
  AdminAuditListQuery,
  AdminAuditTargetKind,
  AdminJobListQuery,
  AdminOrgListQuery,
  AdminOrgSort,
  AdminSortDir,
  AdminUserListQuery,
  AdminUserSort,
  AdminWorkerListQuery,
  AdminWorkerSort,
  UserStatus,
} from "@rivets/core/admin";
import type { JobStatus, OrgStatus } from "@rivets/core";

/**
 * The console's list state, held in the URL (`?q=&status=&sort=&page=…`) so
 * back/forward, reload and a pasted link all restore the exact view.
 *
 * `router.tsx` runs these parsers as each list route's `validateSearch`, which
 * puts them in the main bundle — so everything here is type-only against
 * `@rivets/core/admin` and the tables below mirror its enums. Each table is a
 * `Record` over the core union, so a value added to or dropped from the
 * contract fails to compile here rather than silently not parsing.
 *
 * A parsed search never carries a default (page 1, 50 rows, the list's own
 * sort): parsing normalises, so links stay short and two spellings of the same
 * view are the same URL and the same query key.
 */

export const PAGE_SIZES = [25, 50, 100, 200] as const satisfies typeof ADMIN_PAGE_SIZES;
export const DEFAULT_PAGE_SIZE: typeof ADMIN_DEFAULT_PAGE_SIZE = 50;

/** The direction a column sorts in when first clicked: names A→Z, dates and
 * counts biggest first. */
export const USER_SORTS: Record<AdminUserSort, AdminSortDir> = {
  created: "desc",
  name: "asc",
  email: "asc",
  lastActive: "desc",
};
export const ORG_SORTS: Record<AdminOrgSort, AdminSortDir> = {
  created: "desc",
  name: "asc",
  members: "desc",
  tasks: "desc",
  lastActivity: "desc",
};
export const WORKER_SORTS: Record<AdminWorkerSort, AdminSortDir> = {
  lastSeen: "desc",
  name: "asc",
  created: "desc",
};

const USER_STATUSES: Record<UserStatus, true> = { active: true, suspended: true };
const ORG_STATUSES: Record<OrgStatus, true> = { active: true, suspended: true };
const WORKER_STATUSES: Record<NonNullable<AdminWorkerListQuery["status"]>, true> = { online: true, offline: true };
const TWO_FACTOR: Record<NonNullable<AdminUserListQuery["twoFactor"]>, true> = { on: true, off: true };
export const JOB_STATUSES: Record<JobStatus, true> = {
  queued: true,
  assigned: true,
  running: true,
  completed: true,
  failed: true,
  canceled: true,
};
export const AUDIT_ACTIONS: Record<AdminAuditAction, true> = {
  "org.suspend": true,
  "org.unsuspend": true,
  "org.seats.grant": true,
  "user.suspend": true,
  "user.unsuspend": true,
  "user.super_admin.set": true,
  "user.password_reset": true,
  "user.sessions.revoke": true,
  "prompt.platform.update": true,
};
const AUDIT_TARGET_KINDS: Record<AdminAuditTargetKind, true> = { org: true, user: true, platform: true };

interface PageSearch {
  page?: number;
  pageSize?: number;
}

export interface UserListSearch extends PageSearch {
  q?: string;
  status?: UserStatus;
  superAdmin?: true;
  twoFactor?: "on" | "off";
  membership?: "none";
  sort?: AdminUserSort;
  dir?: AdminSortDir;
}

export interface OrgListSearch extends PageSearch {
  q?: string;
  status?: OrgStatus;
  sort?: AdminOrgSort;
  dir?: AdminSortDir;
}

export interface WorkerListSearch extends PageSearch {
  q?: string;
  orgId?: string;
  status?: "online" | "offline";
  sort?: AdminWorkerSort;
  dir?: AdminSortDir;
}

export interface JobListSearch extends PageSearch {
  q?: string;
  orgId?: string;
  userId?: string;
  workerId?: string;
  status?: JobStatus;
  errored?: true;
}

export interface AuditListSearch extends PageSearch {
  action?: AdminAuditAction;
  targetKind?: AdminAuditTargetKind;
  targetId?: string;
  actorUserId?: string;
}

type RawSearch = Record<string, unknown>;

/** TanStack parses query values as JSON, so `?q=123` arrives as a number and
 * `?errored=true` as a boolean; both are read back as the text they were. */
function text(value: unknown, max = 200): string | undefined {
  const raw = typeof value === "number" || typeof value === "boolean" ? String(value) : value;
  if (typeof raw !== "string") return undefined;
  return raw.trim().slice(0, max) || undefined;
}

function oneOf<T extends string>(table: Record<T, unknown>, value: unknown): T | undefined {
  return typeof value === "string" && Object.hasOwn(table, value) ? (value as T) : undefined;
}

function flag(value: unknown): true | undefined {
  return value === true || value === "true" ? true : undefined;
}

function pageOf(raw: RawSearch): PageSearch {
  const page = Number(raw.page);
  const pageSize = Number(raw.pageSize);
  return {
    page: Number.isInteger(page) && page > 1 ? page : undefined,
    pageSize: (PAGE_SIZES as readonly number[]).includes(pageSize) && pageSize !== DEFAULT_PAGE_SIZE ? pageSize : undefined,
  };
}

/** The sort a list is showing, with the URL's copy dropped where it is the
 * default: the list's own key, and that key's first-click direction. */
function sortOf<K extends string>(
  sorts: Record<K, AdminSortDir>,
  fallback: K,
  raw: RawSearch,
): { sort?: K; dir?: AdminSortDir } {
  const sort = oneOf(sorts, raw.sort) ?? fallback;
  const dir = raw.dir === "asc" || raw.dir === "desc" ? raw.dir : sorts[sort];
  return { sort: sort === fallback ? undefined : sort, dir: dir === sorts[sort] ? undefined : dir };
}

/** Drops absent keys, so a parsed search compares and hashes by what it says. */
function compact<T extends object>(value: T): T {
  return Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as T;
}

export function parseUserSearch(raw: RawSearch): UserListSearch {
  return compact({
    q: text(raw.q),
    status: oneOf(USER_STATUSES, raw.status),
    superAdmin: flag(raw.superAdmin),
    twoFactor: oneOf(TWO_FACTOR, raw.twoFactor),
    membership: raw.membership === "none" ? ("none" as const) : undefined,
    ...sortOf(USER_SORTS, "created", raw),
    ...pageOf(raw),
  });
}

export function parseOrgSearch(raw: RawSearch): OrgListSearch {
  return compact({
    q: text(raw.q),
    status: oneOf(ORG_STATUSES, raw.status),
    ...sortOf(ORG_SORTS, "created", raw),
    ...pageOf(raw),
  });
}

export function parseWorkerSearch(raw: RawSearch): WorkerListSearch {
  return compact({
    q: text(raw.q),
    orgId: text(raw.orgId),
    status: oneOf(WORKER_STATUSES, raw.status),
    ...sortOf(WORKER_SORTS, "lastSeen", raw),
    ...pageOf(raw),
  });
}

export function parseJobSearch(raw: RawSearch): JobListSearch {
  return compact({
    q: text(raw.q),
    orgId: text(raw.orgId),
    userId: text(raw.userId),
    workerId: text(raw.workerId),
    status: oneOf(JOB_STATUSES, raw.status),
    errored: flag(raw.errored),
    ...pageOf(raw),
  });
}

export function parseAuditSearch(raw: RawSearch): AuditListSearch {
  return compact({
    action: oneOf(AUDIT_ACTIONS, raw.action),
    targetKind: oneOf(AUDIT_TARGET_KINDS, raw.targetKind),
    targetId: text(raw.targetId),
    actorUserId: text(raw.actorUserId),
    ...pageOf(raw),
  });
}

// -- URL search → API query ---------------------------------------------------
// Explicit sort, direction, page and size every time: the API's defaults are
// its own business, and the query key should say exactly what was asked for.

/** A list query whose sort is always spelled out, so headers can show it. */
type Sorted<Q, K extends string> = Q & { sort: K; dir: AdminSortDir };

function pageQuery(search: PageSearch) {
  return { page: search.page ?? 1, pageSize: search.pageSize ?? DEFAULT_PAGE_SIZE };
}

export function userListQuery(search: UserListSearch): Sorted<AdminUserListQuery, AdminUserSort> {
  const sort = search.sort ?? "created";
  return compact({
    q: search.q,
    status: search.status,
    superAdmin: search.superAdmin ? ("true" as const) : undefined,
    twoFactor: search.twoFactor,
    membership: search.membership,
    sort,
    dir: search.dir ?? USER_SORTS[sort],
    ...pageQuery(search),
  });
}

export function orgListQuery(search: OrgListSearch): Sorted<AdminOrgListQuery, AdminOrgSort> {
  const sort = search.sort ?? "created";
  return compact({ q: search.q, status: search.status, sort, dir: search.dir ?? ORG_SORTS[sort], ...pageQuery(search) });
}

export function workerListQuery(search: WorkerListSearch): Sorted<AdminWorkerListQuery, AdminWorkerSort> {
  const sort = search.sort ?? "lastSeen";
  return compact({
    q: search.q,
    orgId: search.orgId,
    status: search.status,
    sort,
    dir: search.dir ?? WORKER_SORTS[sort],
    ...pageQuery(search),
  });
}

export function jobListQuery(search: JobListSearch): AdminJobListQuery {
  return compact({
    q: search.q,
    orgId: search.orgId,
    userId: search.userId,
    workerId: search.workerId,
    status: search.status,
    errored: search.errored ? ("true" as const) : undefined,
    ...pageQuery(search),
  });
}

export function auditListQuery(search: AuditListSearch): AdminAuditListQuery {
  return compact({
    action: search.action,
    targetKind: search.targetKind,
    targetId: search.targetId,
    actorUserId: search.actorUserId,
    ...pageQuery(search),
  });
}

/** Whether anything narrows the list — search text or any filter. Sort and
 * paging are how it is read, not what is in it. */
export function isFiltered(search: object): boolean {
  return Object.keys(search).some((key) => !["sort", "dir", "page", "pageSize"].includes(key));
}

/** Everything but sort and page size: what "Clear filters" leaves behind. */
export function clearedFilters<S extends { sort?: unknown; dir?: unknown; pageSize?: number }>(search: S): S {
  return compact({ sort: search.sort, dir: search.dir, pageSize: search.pageSize }) as S;
}

/** The search a header click asks for: the same column flips direction, a new
 * column starts in its own first-click direction. */
export function sortPatch<K extends string>(
  sorts: Record<K, AdminSortDir>,
  current: { sort: K; dir: AdminSortDir },
  key: K,
): { sort: K; dir: AdminSortDir } {
  if (current.sort === key) return { sort: key, dir: current.dir === "asc" ? "desc" : "asc" };
  return { sort: key, dir: sorts[key] };
}

// -- pagination -----------------------------------------------------------------

export type PageItem = number | "gap";

/**
 * The page buttons for `page` of `count`: always the first and last, the
 * current page with a neighbour either side, and an ellipsis for each run
 * skipped — `1 … 4 5 6 … 14`. Past seven pages it is always seven slots, so
 * stepping through never moves Next out from under the pointer; a gap that
 * would hide a single page shows that page instead.
 */
export function pageRange(page: number, count: number): PageItem[] {
  if (count <= 7) return Array.from({ length: count }, (_, index) => index + 1);
  const start = Math.min(Math.max(page - 1, 3), count - 4);
  return [
    1,
    start === 3 ? 2 : "gap",
    start,
    start + 1,
    start + 2,
    start + 2 === count - 2 ? count - 1 : "gap",
    count,
  ];
}

export function pageCount(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

/** "51–100 of 672". */
export function rangeLabel(page: number, pageSize: number, total: number): string {
  if (total === 0) return "0 of 0";
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(total, page * pageSize);
  return `${first.toLocaleString()}–${last.toLocaleString()} of ${total.toLocaleString()}`;
}
